import React, { useState, useEffect, useMemo } from "react"
import { Form, Select, Spin } from "antd"
import Flag from "react-world-flags"
import _ from "lodash"

import CoreRenderFunction from "DisplayCore"
import { checkIfComponentDisabled, getSelectedLanguageContent, handleNestedObjectKey } from "utils"
import "./index.scss"
import { DownOutlined } from "@ant-design/icons"
const { Option } = Select

const TechBaseSelect = (props) => {
  let {
    name,
    label,
    rules,
    style,
    className,
    inputConfig,
    roleConfig,
    children,
    actionKey,
    onSearchActionKey,
    mode,
    loading,
    labelCol,
    wrapperCol
  } = props.item
  const [options, updateOptions] = useState([])
  const [hasMoreOption, setHasMoreOption] = useState(false)
  const [defaultValue, setDefaultValue] = useState(undefined);

  useEffect(() => {   
    if (props[inputConfig.dataKey]) {
      updateOptions(props[inputConfig.dataKey]);
      const nextDefaultValue = typeof inputConfig.defaultValue === "function"
        ? inputConfig.defaultValue(props)
        : inputConfig.defaultValue;

      setDefaultValue(nextDefaultValue);

      if (
        !_.isUndefined(nextDefaultValue) &&
        _.isUndefined(props.form.getFieldValue(name))
      ) {
        props.form.setFieldValue(name, nextDefaultValue);
      }
    } else {
      updateOptions([])
    }
  }, [props[inputConfig.dataKey]])

  const resolvedName = useMemo(() => (
    name[0] === "$" ? handleNestedObjectKey(props, name.slice(1)) :
      _.isArray(name) ? name.map(n => n[0] === "$" ? handleNestedObjectKey(props, n.slice(1)) : n) :
        name
  ), [name, props])

  const showSelectAll = mode === "multiple" && inputConfig.allowSelectAll === true
  const contextForm = Form.useFormInstance()
  const activeForm = contextForm || props.form
  const watchedValue = Form.useWatch(resolvedName, activeForm)
  const allValues = useMemo(() => {
    const valueKey = inputConfig.value || "value"
    const fromStatic = (inputConfig.options || []).map(o => o.value)
    const fromDynamic = options.map(o => o[valueKey])
    return [...fromStatic, ...fromDynamic]
  }, [inputConfig.options, options, inputConfig.value])
  const allSelected = Array.isArray(watchedValue) && allValues.length > 0 && watchedValue.length === allValues.length

  const handleToggleSelectAll = () => {
    const next = allSelected ? [] : allValues
    activeForm?.setFieldValue(resolvedName, next)
    if (props.onClickAction && actionKey) {
      props.onClickAction(actionKey, next)
    }
  }

  const checkIsDisabled = () => {
    if (inputConfig.isDisabled) {
      return checkIfComponentDisabled(props.disabledComponent, inputConfig)
    }
    return inputConfig.disabled || (roleConfig && props.disabled)
  }

  const handlePopupScroll = (e) => {
    const target = e.target
    setHasMoreOption(target.scrollHeight - target.scrollTop > target.clientHeight)
  }

  return (
    <Form.Item
      labelCol={labelCol}
      wrapperCol={wrapperCol}
      name={ resolvedName }
      className={className}
      label={getSelectedLanguageContent(label)}
      style={style}
      rules={rules?.map((rule) => ({
        ...rule,
        message: getSelectedLanguageContent(rule.message, props.selectedLanguage),
      }))}
    >
      <Select
        mode={mode}
        suffixIcon={handleNestedObjectKey(props, loading) && <Spin />}
        showSearch={_.isUndefined(inputConfig.showSearch) ? true : inputConfig.showSearch}
        allowClear={inputConfig.allowClear}
        disabled={checkIsDisabled()}
        placement="bottomLeft"
        size={inputConfig.size}
        open={inputConfig.open}
        style={inputConfig.style}
        className={(inputConfig.className || "") + (inputConfig.showFlag ? " show-flag" : "")}
        getPopupContainer={(trigger) => trigger.parentElement}
        placeholder={getSelectedLanguageContent(inputConfig.placeholder)}
        showArrow={inputConfig.showArrow}
        defaultActiveFirstOption={inputConfig.defaultActiveFirstOption}
        defaultValue={defaultValue}
        optionLabelProp={children ? "label" : undefined}
        onSearch={
          props.onClickAction
            ? (value) => {
                props.onClickAction(onSearchActionKey, value)
              }
            : null
        }
        onChange={
          props.onClickAction
            ? (value) => {
                props.onClickAction(actionKey, value)
              }
            : null
        }
        onPopupScroll={handlePopupScroll}
        filterOption={(input, option) => {
          if (inputConfig.filterKeys && option.searchData !== undefined) {
            return option.searchData.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          return (
            (typeof option.value === "number"
              ? option.value.toString().indexOf(input.toLowerCase()) >= 0
              : option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0) ||
            (typeof option.children[1] === "number"
              ? option.children[1].toString().indexOf(input.toLowerCase()) >= 0
              : (typeof option.children[1] !== 'string' ? option.label : option.children[1]).toLowerCase().indexOf(input.toLowerCase()) >= 0 )
          )
        }}
        dropdownRender={(menu) => (
          <div>
            {showSelectAll && allValues.length > 0 && (
              <div
                role="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleToggleSelectAll}
                style={{
                  padding: "5px 12px",
                  cursor: "pointer",
                  color: "#1890ff",
                  borderBottom: "1px solid #f0f0f0",
                  userSelect: "none"
                }}
              >
                {getSelectedLanguageContent(allSelected ? "{{DESELECT_ALL}}" : "{{SELECT_ALL}}")}
              </div>
            )}
            {menu}
            {hasMoreOption && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <DownOutlined style={{ color: "#b3b3b3" }} />
              </div>
            )}
          </div>
        )}
      >
        {inputConfig.isNullable && <Option key={"null"} value={null}></Option>}
        {inputConfig.options?.map((option) => (
          <option value={option.value}>{getSelectedLanguageContent(option.label)}</option>
        ))}
        {options.map((option) => (
          <Option
            key={option.id}
            value={option[inputConfig.value || "value"]}
            label={option[inputConfig.label || "label"]}
            {...(inputConfig.filterKeys ? { searchData: inputConfig.filterKeys.map(k => option[k] ?? "").join(" ") } : {})}
          >
            {inputConfig.showFlag && (
              <div style={{ width: "35px" }} className="mr-2">
                <Flag code={option.iso2} />
              </div>
            )}
            {children
              ? children.map((child) => <CoreRenderFunction {...props} item={child} selectedItem={option} />)
              : getSelectedLanguageContent(option[inputConfig.label || "label"])}
          </Option>
        ))}
      </Select>
    </Form.Item>
  )
}

export default TechBaseSelect
